import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@apollo/client/react";
import { Loader2, Wand2, X } from "lucide-react";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { gradeLabel } from "@/lib/students";
import {
  GET_PLACEMENT_TESTS,
  GET_SKILL_SETUP_PROPOSAL,
  GET_STUDENT_SKILLS,
} from "@/graphql/queries/students";
import { APPLY_SKILL_SETUP } from "@/graphql/mutations/students";
import { SkillStatusControl, STATUS_META, type SkillStatus } from "@/components/skills/skill-status";
import { PlacementTestPanel, type PlacementTest } from "@/components/skills/placement-test";

type Proposal = {
  skillKey: string;
  subject: string;
  subjectName: string | null;
  grade: number;
  name: string | null;
  status: SkillStatus;
  currentStatus: SkillStatus;
  changed: boolean;
  source: string;
};

type Student = {
  name: string;
  grade: number | null;
  gradeKnown: boolean;
  skillSetupDone: boolean;
};

/**
 * Where a learner starts, on one screen.
 *
 * A new account has a row for every skill at zero, which describes the config
 * and not the child: a ten-year-old is not starting arithmetic from nothing.
 * Saying so used to mean finding a bulk-edit mode, filtering to a grade,
 * selecting it and typing a percentage — which is why nobody did it, and why
 * every session for a new learner began by guessing.
 *
 * Nothing here is applied until it is read. The proposal is the app's opening
 * offer; the whole screen exists so a grown-up can disagree with it before it
 * becomes true.
 */
export function SkillSetupWizard({
  studentId,
  student,
  onClose,
}: {
  studentId: string;
  student: Student;
  onClose: () => void;
}) {
  const { data, loading, refetch } = useQuery(GET_SKILL_SETUP_PROPOSAL, {
    variables: { studentId },
    fetchPolicy: "network-only",
  });

  // The tests taken before this screen was opened. A test finished *during* it
  // hands its own state back from the mutation, so this is only the way in.
  const { data: placementData, refetch: refetchPlacement } = useQuery(GET_PLACEMENT_TESTS, {
    variables: { studentId },
    fetchPolicy: "network-only",
  });

  const proposals: Proposal[] = useMemo(
    () => data?.skillSetupProposal ?? [],
    [data]
  );

  const placementTests: PlacementTest[] = useMemo(
    () => placementData?.placementTests ?? [],
    [placementData]
  );

  // The subjects the proposal actually covers, in its own order — a test for a
  // subject nothing is being proposed for would measure work this screen cannot
  // apply.
  const placementSubjects = useMemo(() => {
    const seen: { id: string; name: string }[] = [];
    proposals.forEach((proposal) => {
      if (!seen.some((entry) => entry.id === proposal.subject)) {
        seen.push({ id: proposal.subject, name: proposal.subjectName ?? proposal.subject });
      }
    });
    return seen;
  }, [proposals]);

  // Whether any of the offer came out of the learner's own answers. A count is
  // deliberately not shown: a finished test sources the whole of its subject,
  // including every „dar ne" above the ceiling, so the number is in the
  // hundreds and reads as a claim about how much was asked.
  const measured = proposals.some((p) => p.source === "placement");

  // Overrides the grown-up has made, keyed by skill. Absent means "as proposed".
  const [overrides, setOverrides] = useState<Record<string, SkillStatus>>({});
  const [showAll, setShowAll] = useState(false);

  // A fresh proposal is a fresh conversation — carrying overrides across one
  // would silently apply decisions made about a different set of skills.
  useEffect(() => setOverrides({}), [proposals]);

  const [applySkillSetup, { loading: applying }] = useMutation(APPLY_SKILL_SETUP, {
    refetchQueries: [{ query: GET_STUDENT_SKILLS, variables: { id: studentId } }],
    onError: (error) => toast.error(`Klaida: ${error.message}`),
  });

  const statusOf = (proposal: Proposal): SkillStatus =>
    overrides[proposal.skillKey] ?? proposal.status;

  const changes = proposals.filter((p) => statusOf(p) !== p.currentStatus);
  const counts = changes.reduce<Record<string, number>>((acc, p) => {
    const status = statusOf(p);
    acc[status] = (acc[status] ?? 0) + 1;
    return acc;
  }, {});

  const visible = showAll ? proposals : proposals.filter((p) => statusOf(p) !== p.currentStatus);

  // Subject, then grade — the order somebody thinks about a curriculum in.
  const grouped = useMemo(() => {
    const bySubject = new Map<string, Map<number, Proposal[]>>();
    const names = new Map<string, string>();
    visible.forEach((proposal) => {
      names.set(proposal.subject, proposal.subjectName ?? proposal.subject);
      const grades = bySubject.get(proposal.subject) ?? new Map<number, Proposal[]>();
      const list = grades.get(proposal.grade) ?? [];
      list.push(proposal);
      grades.set(proposal.grade, list);
      bySubject.set(proposal.subject, grades);
    });
    return [...bySubject.entries()].map(
      ([subject, grades]) =>
        [
          names.get(subject) ?? subject,
          [...grades.entries()].sort((a, b) => a[0] - b[0]),
        ] as const
    );
  }, [visible]);

  const setGrade = (list: Proposal[], status: SkillStatus) => {
    setOverrides((prev) => {
      const next = { ...prev };
      list.forEach((p) => {
        next[p.skillKey] = status;
      });
      return next;
    });
  };

  const apply = () => {
    if (changes.length === 0) return;

    applySkillSetup({
      variables: {
        studentId,
        statuses: changes.map((p) => ({ skillKey: p.skillKey, status: statusOf(p) })),
      },
      onCompleted: (result) => {
        const errors: string[] = result.applySkillSetup.errors ?? [];
        if (errors.length > 0) {
          toast.error(errors.join(", "));
          return;
        }
        toast.success(`Nustatyta ${result.applySkillSetup.changedCount} įgūdžių`);
        onClose();
      },
    });
  };

  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Wand2 className="h-4 w-4 text-primary" />
              Ką {student.name} jau moka
            </CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              {measured ? (
                <>
                  {/* A measured offer and a guessed one are read very
                      differently, so the screen says which it is. */}
                  Pasiūlymas perskaitytas iš patikros: mokinio atsakymai, o kur
                  jų nebuvo — tos klasės rezultatas. Nieko neįrašoma, kol
                  nepaspausi.
                </>
              ) : student.grade != null ? (
                <>
                  Pagal {gradeLabel(student.grade)}
                  {!student.gradeKnown && " (spėjama pagal gimimo metus — verta įrašyti, kada pradėjo 1 klasę)"}
                  : ankstesnės klasės laikomos išmoktomis, viena prieš — dirbama, toliau — dar ne.
                  Nieko neįrašoma, kol nepaspausi.
                </>
              ) : (
                "Nežinome, kurioje klasėje mokinys — įrašyk gimimo metus arba kada pradėjo 1 klasę."
              )}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="shrink-0">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : proposals.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Nėra ką siūlyti — trūksta klasės arba mokinys neturi dalykų.
          </p>
        ) : (
          <>
            {/* Above the proposal, not beside it: the offer below is derived
                from whatever this panel has found, so a grown-up who reads the
                offer first is reading a number whose source is off screen. */}
            <PlacementTestPanel
              studentId={studentId}
              name={student.name}
              subjects={placementSubjects}
              tests={placementTests}
              onFinished={() => {
                refetch();
                refetchPlacement();
              }}
            />

            <div className="flex flex-wrap items-center gap-2 rounded-md bg-muted/40 px-3 py-2 text-sm">
              {changes.length === 0 ? (
                <span className="text-muted-foreground">
                  Nieko keisti nesiūlome — viskas jau nustatyta.
                </span>
              ) : (
                <>
                  <span className="text-muted-foreground">Pasikeis:</span>
                  {(["known", "learning", "not_yet"] as SkillStatus[]).map(
                    (status) =>
                      counts[status] > 0 && (
                        <Badge key={status} variant="secondary" className="font-normal">
                          {counts[status]} → {STATUS_META[status].label.toLowerCase()}
                        </Badge>
                      )
                  )}
                </>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAll(!showAll)}
                className="ml-auto h-7 text-xs"
              >
                {showAll ? "Rodyti tik pokyčius" : `Rodyti visus (${proposals.length})`}
              </Button>
            </div>

            <div className="max-h-[28rem] space-y-4 overflow-y-auto pr-1">
              {grouped.map(([subjectLabel, grades]) => (
                <div key={subjectLabel} className="space-y-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {subjectLabel}
                  </h4>
                  {grades.map(([grade, list]) => (
                    <div key={grade} className="rounded-md border">
                      <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-1.5">
                        <span className="text-sm font-medium">{gradeLabel(grade)}</span>
                        <span className="text-xs text-muted-foreground">{list.length}</span>
                        <div className="ml-auto">
                          {/* Whole grades are how somebody actually thinks about
                              this — "she has done everything up to fourth". */}
                          <SkillStatusControl
                            value={list.every((p) => statusOf(p) === statusOf(list[0])) ? statusOf(list[0]) : "learning"}
                            onChange={(status) => setGrade(list, status)}
                            disabled={applying}
                            compact
                          />
                        </div>
                      </div>
                      <div className="divide-y">
                        {list.map((proposal) => {
                          const status = statusOf(proposal);
                          return (
                            <div
                              key={proposal.skillKey}
                              className="flex items-center gap-2 px-3 py-1.5"
                            >
                              <span className="min-w-0 flex-1 truncate text-sm">
                                {proposal.name ?? proposal.skillKey}
                              </span>
                              {status !== proposal.currentStatus && (
                                <span className="shrink-0 text-[10px] text-muted-foreground">
                                  {STATUS_META[proposal.currentStatus].short} →
                                </span>
                              )}
                              <SkillStatusControl
                                value={status}
                                onChange={(next) =>
                                  setOverrides((prev) => ({ ...prev, [proposal.skillKey]: next }))
                                }
                                disabled={applying}
                                compact
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
              {visible.length === 0 && (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  Nėra pokyčių. Spausk „Rodyti visus", jei nori pakeisti ką nors rankiniu būdu.
                </p>
              )}
            </div>

            <div className="flex items-center gap-2 border-t pt-3">
              <Button onClick={apply} disabled={applying || changes.length === 0}>
                {applying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Pritaikyti ({changes.length})
              </Button>
              <Button variant="ghost" onClick={onClose} disabled={applying}>
                Atšaukti
              </Button>
              <p className={cn("ml-auto text-xs text-muted-foreground")}>
                Pažymėtų įgūdžių užduočių sesijos nebeduos.
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
